import React, { useEffect, useReducer, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useDataProvider } from "react-admin";
import { validate } from "./validate";
import { buildPayload } from "./buildPayload";
import { extractApiErrorMessage } from "./errors";
import AudienceStep from "./steps/AudienceStep";
import ContentStep from "./steps/ContentStep";
import PushStep from "./steps/PushStep";
import ReviewStep from "./steps/ReviewStep";
import ConfirmPushDialog, { isWideAudience } from "./ConfirmPushDialog";
import ResultPanel from "./ResultPanel";

// Step numbers mirror validate.js's private step numbering (tasks.md T8):
// 1 Audience, 2 Content, 3 Push, 4 Review & Send.
const AUDIENCE_STEP = 1;
const CONTENT_STEP = 2;
const PUSH_STEP = 3;
const REVIEW_STEP = 4;
const FIRST_STEP = AUDIENCE_STEP;
const LAST_STEP = REVIEW_STEP;

const STEP_LABELS = ["Audience", "Content", "Push", "Review & Send"];

// A factory, not a shared constant: RESET and the initial useReducer call
// each need their own fresh audience_roles array, so a future in-place
// mutation (e.g. .push on a role list) can't corrupt a value shared across
// composer instances or send/reset cycles.
const createInitialState = () => ({
  step: FIRST_STEP,
  submitStatus: "idle",
  submitError: null,
  submitResult: null,
  audience_type: "all_users",
  audience_roles: [],
  user_ids: "",
  workspace_id: "",
  message: "",
  action_url: "",
  type: "general.new.article",
  category: "general",
  send_push_notification: false,
  push_title: "",
  push_subtitle: "",
});

export function reducer(state, action) {
  switch (action.type) {
    case "SET_FIELD":
      return {
        ...state,
        [action.field]: action.value,
        // NOTIF-22 — editing any field after an unknown-outcome send re-arms
        // Send: the operator is now about to send a request that differs
        // from the one whose outcome was never confirmed, so the risk the
        // disablement guards against no longer applies as-is.
        submitStatus: state.submitStatus === "unknown" ? "idle" : state.submitStatus,
      };
    // Computed from the reducer's own `state` argument (always the latest
    // accumulated value) rather than a component-level closure, so two
    // TOGGLE_ROLE actions dispatched in the same tick still apply in order
    // instead of one silently clobbering the other.
    case "TOGGLE_ROLE": {
      const roles = state.audience_roles || [];
      const nextRoles = roles.includes(action.role)
        ? roles.filter((existing) => existing !== action.role)
        : [...roles, action.role];
      return {
        ...state,
        audience_roles: nextRoles,
        submitStatus: state.submitStatus === "unknown" ? "idle" : state.submitStatus,
      };
    }
    case "NEXT":
      return state.step < LAST_STEP ? { ...state, step: state.step + 1 } : state;
    case "BACK":
      return state.step > FIRST_STEP ? { ...state, step: state.step - 1 } : state;
    case "SUBMIT_START":
      return { ...state, submitStatus: "submitting", submitError: null };
    case "SUBMIT_SUCCESS":
      return { ...state, submitStatus: "success", submitResult: action.result };
    // 400/500/unparseable (NOTIF-21) — settles back out of "submitting" so a
    // corrected retry is possible; the error message itself is rendered
    // wherever submitStatus === "error" is checked, below.
    case "SUBMIT_ERROR":
      return { ...state, submitStatus: "error", submitError: action.message };
    // No HTTP status (network/timeout) — NOTIF-22 keeps Send disabled and
    // renders the unknown-outcome banner wherever submitStatus === "unknown"
    // is checked, below; re-armed by SET_FIELD above or the "Send anyway" ack.
    case "SUBMIT_UNKNOWN":
      return { ...state, submitStatus: "unknown" };
    case "RESET":
      return createInitialState();
    default:
      return state;
  }
}

export default function Composer() {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState);
  const dataProvider = useDataProvider();
  const submittingRef = useRef(false);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [wideConfirmed, setWideConfirmed] = useState(false);
  const [sendAnywayOpen, setSendAnywayOpen] = useState(false);

  // NOTIF-12 AC9 — the second confirmation is not sticky: once armed by a
  // real change to audience or push, a prior confirmation no longer covers
  // the next send attempt (context.md "Confirmation gates"). audience_roles
  // is included because it changes the resolved audience the dialog names
  // (describeAudience) even when audience_type/workspace_id stay the same.
  useEffect(() => {
    setWideConfirmed(false);
  }, [state.audience_type, state.workspace_id, state.audience_roles, state.send_push_notification]);

  // NOTIF-19/NOTIF-20 — any 200 response (recipients > 0 or exactly 0)
  // replaces the stepper with the terminal result panel; ResultPanel itself
  // branches on the zero-recipient case so it isn't framed as a success.
  if (state.submitStatus === "success") {
    return (
      <ResultPanel
        result={state.submitResult}
        onComposeAnother={() => dispatch({ type: "RESET" })}
      />
    );
  }

  const errors = validate(state, state.step);
  const canAdvance = Object.keys(errors).length === 0;

  // Single-flight guard (NOTIF-18): a plain state check is one render behind
  // — several clicks fired before React flushes the "submitting" state would
  // all read the same stale value. The ref is checked and set synchronously,
  // so only the very first of any burst of clicks proceeds.
  const submit = async () => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    dispatch({ type: "SUBMIT_START" });
    try {
      const payload = buildPayload(state);
      const result = await dataProvider.createInboxNotification(payload);
      dispatch({ type: "SUBMIT_SUCCESS", result: result.data });
    } catch (error) {
      if (error?.status) {
        dispatch({ type: "SUBMIT_ERROR", message: extractApiErrorMessage(error) });
      } else {
        dispatch({ type: "SUBMIT_UNKNOWN" });
      }
    } finally {
      submittingRef.current = false;
    }
  };

  // NOTIF-12 AC7/8 (revised 2026-08-17) — a wide send (all_users, or workspace
  // roles across every workspace) now requires this confirmation regardless
  // of push: it's the highest-blast-radius option and the zero-interaction
  // default, so the step-4 review being the only gate (when push is off) was
  // judged too thin a safeguard for an irreversible, database-wide send.
  const handleSendClick = () => {
    if (isWideAudience(state) && !wideConfirmed) {
      setPushDialogOpen(true);
      return;
    }
    submit();
  };

  const handleConfirmPush = () => {
    setWideConfirmed(true);
    setPushDialogOpen(false);
    submit();
  };

  return (
    // pt clears the fixed top AppBar: rendered as a bare `list` (App.js), this
    // composer gets none of react-admin <List>'s default top spacing, so the
    // Stepper would otherwise sit flush against — and clip under — the nav bar.
    <Box sx={{ pt: 2 }}>
      <Stepper activeStep={state.step - 1}>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mt: 3 }} data-testid={`step-${state.step}-body`}>
        {state.step === AUDIENCE_STEP && (
          <AudienceStep state={state} dispatch={dispatch} errors={errors} />
        )}
        {state.step === CONTENT_STEP && (
          <ContentStep state={state} dispatch={dispatch} errors={errors} />
        )}
        {state.step === PUSH_STEP && (
          <PushStep state={state} dispatch={dispatch} errors={errors} />
        )}
        {state.step === REVIEW_STEP && (
          <ReviewStep
            state={state}
            submitting={state.submitStatus === "submitting"}
            // `canAdvance` at REVIEW_STEP is validate(state, REVIEW_STEP) —
            // the same full-form guard the stepper used to reach this step.
            // Re-asserting it directly on Send (defense-in-depth) means the
            // guard holds even if a future change adds a way to land on step
            // 4 other than advancing sequentially through 1-3.
            disabled={state.submitStatus === "unknown" || !canAdvance}
            onSendClick={handleSendClick}
          />
        )}

        {/* NOTIF-21 — 400/500/unparseable: the composer stays put, values
            stay put (no reducer path clears a field here), and the message
            extractApiErrorMessage derived is shown alongside the send action. */}
        {state.step === REVIEW_STEP && state.submitStatus === "error" && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="submit-error-banner">
            {state.submitError}
          </Alert>
        )}

        {/* NOTIF-22 — no HTTP status (network error / timeout): no terminal
            panel, no auto-retry, Send stays disabled (enforced via the
            `disabled` prop above) until either a field edit re-arms it
            (reducer's SET_FIELD case) or this ack-gated secondary is used. */}
        {state.step === REVIEW_STEP && state.submitStatus === "unknown" && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="unknown-outcome-banner">
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Outcome unknown
            </Typography>
            <Typography variant="body2">
              The request never received a response, so it is not known whether this notification
              was already accepted. Re-sending risks creating a duplicate that cannot be recalled
              — nothing is retried automatically.
            </Typography>
            <Typography variant="body2">
              Edit any field to try again, or use the option below if a re-send is acceptable.
            </Typography>
            <Button
              size="small"
              color="warning"
              sx={{ mt: 1 }}
              onClick={() => setSendAnywayOpen(true)}
            >
              Send anyway — may duplicate
            </Button>
          </Alert>
        )}
      </Box>

      <Box sx={{ mt: 3, display: "flex", gap: 1 }}>
        <Button onClick={() => dispatch({ type: "BACK" })} disabled={state.step === FIRST_STEP}>
          Back
        </Button>
        {state.step < REVIEW_STEP && (
          <Button variant="contained" onClick={() => dispatch({ type: "NEXT" })} disabled={!canAdvance}>
            Next
          </Button>
        )}
      </Box>

      <ConfirmPushDialog
        open={pushDialogOpen}
        state={state}
        onCancel={() => setPushDialogOpen(false)}
        onConfirm={handleConfirmPush}
      />

      <Dialog open={sendAnywayOpen} onClose={() => setSendAnywayOpen(false)}>
        <DialogTitle>Send anyway?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The previous attempt&apos;s outcome is unknown — it may already have been accepted.
            Sending again may create a duplicate notification that cannot be recalled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendAnywayOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setSendAnywayOpen(false);
              submit();
            }}
          >
            Send anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
